import { AssigneeFilterComponent, AssigneeFilterValue, EMPTY_ASSIGNEE_FILTER } from '../../company/assignee-filter/assignee-filter';
import { CompanyContextService } from '../../../services/company-context';
import { DealAssigneeFilter } from '../../../services/api/deal';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { DealService, CargoDeal } from '../../../services/api/deal';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { FfCabinetHeadComponent } from '../cabinet-head/cabinet-head.component';
import { PageResponse } from '../../../services/api/page-response';
import { DocNumberPipe } from '../../../services/request-number';
import { ResizableColumnsDirective } from '../../../services/resizable-columns.directive';
import { isDeliveredByCodes, visibleActivities } from '../../../services/deal-stage';

@Component({
  selector: 'app-freight-forwarder-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, AssigneeFilterComponent, PaginationComponent, FfCabinetHeadComponent, DocNumberPipe, ResizableColumnsDirective],
  templateUrl: './freight-forwarder-dashboard.html',
  styleUrl: './freight-forwarder-dashboard.scss'
})
export class FreightForwarderDashboardComponent implements OnInit, OnDestroy {
  activeTab: 'bids' | 'deals' = 'bids';
  
  // Ставки
  bids: FreightForwarderBidDto[] = [];
  loadingBids = false;
  errorBids: string | null = null;
  bidsPage: PageResponse<FreightForwarderBidDto> | null = null;
  currentBidsPage = 0;
  pageSize = 20;
  
  // Сделки
  deals: CargoDeal[] = [];
  loadingDeals = false;
  errorDeals: string | null = null;
  dealsPage: PageResponse<CargoDeal> | null = null;
  currentDealsPage = 0;

  // Translations
  titleText = 'Мой кабинет';
  bidsTabText = 'Мои ставки';
  dealsTabText = 'Мои сделки';
  loadingText = 'Загрузка...';
  noBidsText = 'У вас пока нет ставок';
  noDealsText = 'У вас пока нет активных сделок';
  bidNumberText = 'Ставка';
  requestText = 'Запрос';
  priceText = 'Цена';
  deliveryDaysText = 'Срок';
  daysText = 'дней';
  statusText = 'Статус';
  createdAtText = 'Дата';
  viewText = 'Просмотр';
  dealNumberText = 'Сделка';
  cargoOwnerText = 'Грузовладелец';
  pendingText = 'Ожидает';
  acceptedText = 'Принята';
  lostText = 'Проиграла';
  pricePositionText = 'Позиция по цене';

  routeText = 'Маршрут';

  /** Заявки по номеру: объект, null — заявка недоступна (удалена); пока грузится — ключа нет. */
  requests: Record<number, CargoRequest | null> = {};

  /** По сделкам: цена ставки и текущий этап (берём из карточки сделки — в списке их нет). */
  dealInfo: Record<number, { price: string; stage: string; stageDate: string | null; updatedToday: boolean; delivered: boolean } | null> = {};

  /** Быстрый фильтр ставок по статусу. */
  bidFilter: 'all' | 'PENDING' | 'ACCEPTED' | 'LOST' = 'all';
  private countries: CountryDto[] | null = null;

  private subscriptions = new Subscription();

  constructor(
    private bidService: FreightForwarderBidService,
    private dealService: DealService,
    private cargoRequestService: CargoRequestService,
    private dictionaryService: DictionaryService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('FreightForwarderDashboard: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
        return;
      }

      // Читаем активную вкладку из URL
      this.subscriptions.add(
        this.route.paramMap.subscribe(params => {
          const tab = params.get('tab') as 'bids' | 'deals';
          if (tab && ['bids', 'deals'].includes(tab)) {
            this.activeTab = tab;
            this.loadTabData(tab);
          }
          this.cdr.markForCheck();
        })
      );

      this.updateTranslations();

      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
          this.cdr.markForCheck();
        })
      );
    }, 0);
  }

  private loadTabData(tab: 'bids' | 'deals'): void {
    if (tab === 'bids' && this.bids.length === 0 && !this.loadingBids) {
      this.loadBids();
    } else if (tab === 'deals' && this.deals.length === 0 && !this.loadingDeals) {
      this.loadDeals();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('freightForwarder.dashboard.title') || 'Мой кабинет';
    this.bidsTabText = this.languageService.translate('freightForwarder.dashboard.bidsTab') || 'Мои ставки';
    this.dealsTabText = this.languageService.translate('freightForwarder.dashboard.dealsTab') || 'Мои сделки';
    this.loadingText = this.languageService.translate('freightForwarder.dashboard.loading') || 'Загрузка...';
    this.noBidsText = this.languageService.translate('freightForwarder.dashboard.noBids') || 'У вас пока нет ставок';
    this.noDealsText = this.languageService.translate('freightForwarder.dashboard.noDeals') || 'У вас пока нет активных сделок';
    this.bidNumberText = this.languageService.translate('freightForwarder.dashboard.bidNumber') || 'Ставка';
    this.requestText = this.languageService.translate('freightForwarder.dashboard.request') || 'Запрос';
    this.priceText = this.languageService.translate('freightForwarder.dashboard.price') || 'Цена';
    this.deliveryDaysText = this.languageService.translate('freightForwarder.dashboard.deliveryDays') || 'Срок';
    this.daysText = this.languageService.translate('freightForwarder.dashboard.days') || 'дней';
    this.statusText = this.languageService.translate('freightForwarder.dashboard.status') || 'Статус';
    this.createdAtText = this.languageService.translate('freightForwarder.dashboard.createdAt') || 'Дата';
    this.viewText = this.languageService.translate('freightForwarder.dashboard.view') || 'Просмотр';
    this.dealNumberText = this.languageService.translate('freightForwarder.dashboard.dealNumber') || 'Сделка';
    this.cargoOwnerText = this.languageService.translate('freightForwarder.dashboard.cargoOwner') || 'Грузовладелец';
    this.pendingText = this.languageService.translate('freightForwarder.dashboard.pending') || 'Ожидает';
    this.acceptedText = this.languageService.translate('freightForwarder.dashboard.accepted') || 'Принята';
    this.lostText = this.languageService.translate('freightForwarder.dashboard.lost') || 'Проиграла';
    this.pricePositionText = this.languageService.translate('freightForwarder.dashboard.pricePosition') || 'Позиция по цене';
    const route = this.languageService.translate('freightForwarder.requests.route');
    this.routeText = route && route !== 'freightForwarder.requests.route' ? route : 'Маршрут';
  }

  switchTab(tab: 'bids' | 'deals'): void {
    // Навигация на новый URL - компонент перезагрузит данные через paramMap subscription
    this.router.navigate(['/freight-forwarder/dashboard', tab]);
  }

  loadBids(): void {
    this.loadBidsPage(this.currentBidsPage);
  }

  loadBidsPage(page: number): void {
    this.loadingBids = true;
    this.errorBids = null;
    this.currentBidsPage = page;

    this.subscriptions.add(
      this.bidService.getAllBidsPaginated(page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.bidsPage = pageResponse;
          this.bids = pageResponse.content;
          this.loadRoutes(this.bids.map(b => b.cargoRequestId));
          this.loadingBids = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading bids:', err);
          this.errorBids = 'Ошибка при загрузке ставок';
          this.loadingBids = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /**
   * В ставках и сделках сервер не отдаёт маршрут — подгружаем его из самих заявок (по одной на номер, с кешем).
   * Лучше, чтобы бэкенд добавил маршрут в ответ, тогда этот запрос можно убрать.
   */
  private loadRoutes(requestIds: number[]): void {
    const ids = [...new Set(requestIds)].filter(id => id && !(id in this.requests));
    if (!ids.length) {
      return;
    }
    const countries$ = this.countries ? of(this.countries) : this.dictionaryService.getAllCountries().pipe(catchError(() => of([] as CountryDto[])));
    const requests$ = forkJoin(ids.map(id => this.cargoRequestService.getCargoRequest(id).pipe(catchError(() => of(null)))));

    this.subscriptions.add(
      forkJoin([countries$, requests$]).subscribe(([countries, requests]) => {
        this.countries = countries;
        ids.forEach((id, i) => {
          this.requests[id] = requests[i];
        });
        this.cdr.markForCheck();
      })
    );
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  countryName(id: number | undefined): string {
    const c = this.countries?.find(x => x.id === id);
    if (!c) return '';
    return this.languageService.getCurrentLanguageValue() === 'en' ? (c.nameEn || c.nameRu) : (c.nameRu || c.nameEn);
  }

  /** Заявка загружена и её нет (удалена или недоступна). */
  isRequestGone(id: number): boolean {
    return id in this.requests && this.requests[id] === null;
  }

  requestCreatedAt(bid: FreightForwarderBidDto): string | undefined {
    return this.requests[bid.cargoRequestId]?.createdAt || bid.createdAt;
  }

  money(amount: number, code: string): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount || 0)} ${code || ''}`.trim();
  }

  get bidFilters(): { key: 'all' | 'PENDING' | 'ACCEPTED' | 'LOST'; label: string }[] {
    return [
      { key: 'all', label: this.t('ffDash.all', 'Все') },
      { key: 'PENDING', label: this.t('ffDash.pending', 'Отправлены') },
      { key: 'ACCEPTED', label: this.t('ffDash.accepted', 'Приняты') },
      { key: 'LOST', label: this.t('ffDash.lost', 'Не выбраны') },
    ];
  }

  private bidState(bid: FreightForwarderBidDto): 'PENDING' | 'ACCEPTED' | 'LOST' {
    return bid.bidStatus === 'ACCEPTED' || bid.bidStatus === 'LOST' ? bid.bidStatus : 'PENDING';
  }

  bidCount(key: 'all' | 'PENDING' | 'ACCEPTED' | 'LOST'): number {
    return key === 'all' ? this.bids.length : this.bids.filter(b => this.bidState(b) === key).length;
  }

  get filteredBids(): FreightForwarderBidDto[] {
    return this.bidFilter === 'all' ? this.bids : this.bids.filter(b => this.bidState(b) === this.bidFilter);
  }

  /** Клик по строке: открыть запрос (если он ещё существует). */
  openBidRow(bid: FreightForwarderBidDto): void {
    if (this.isRequestGone(bid.cargoRequestId)) return;
    this.viewBid(bid);
  }

  onBidsPageChange(page: number): void {
    this.loadBidsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Ответственные сотрудники (распределение внутри компании) ──
  dealManagerFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;
  dealOperatorFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;

  /** Администратор компании: видит менеджеров сделок (кто сделал ставку) и фильтрует по ним. */
  get showManagerColumn(): boolean {
    return this.companyContext.hasPermission('ASSIGN_REQUESTS');
  }

  /** Администратор и менеджер: видят операторов сделок и фильтруют по ним. */
  get showOperatorColumn(): boolean {
    return this.companyContext.hasPermission('ASSIGN_DEALS');
  }

  onDealFilterChange(): void {
    this.loadDealsPage(0);
  }

  private dealFilterParams(): DealAssigneeFilter {
    return {
      managerId: this.dealManagerFilter.id,
      operatorId: this.dealOperatorFilter.id,
      operatorUnassigned: this.dealOperatorFilter.unassigned
    };
  }

  loadDeals(): void {
    this.loadDealsPage(this.currentDealsPage);
  }

  loadDealsPage(page: number): void {
    this.loadingDeals = true;
    this.errorDeals = null;
    this.currentDealsPage = page;

    this.subscriptions.add(
      this.dealService.getMyDealsForwarderPaginated(page, this.pageSize, this.dealFilterParams()).subscribe({
        next: (pageResponse) => {
          this.dealsPage = pageResponse;
          this.deals = pageResponse.content;
          this.loadRoutes(this.deals.map(d => d.cargoRequestId));
          this.loadDealInfo(this.deals.map(d => d.id));
          this.loadingDeals = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading deals:', err);
          this.errorDeals = 'Ошибка при загрузке сделок';
          this.loadingDeals = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Цена и последний этап по каждой сделке на странице (по одной карточке на сделку). */
  private loadDealInfo(ids: number[]): void {
    const todo = ids.filter(id => !(id in this.dealInfo));
    if (!todo.length) return;
    const lang = this.languageService.getCurrentLanguageValue();
    this.subscriptions.add(
      forkJoin(todo.map(id => this.dealService.getDealDetail(id).pipe(catchError(() => of(null))))).subscribe(details => {
        details.forEach((d, i) => {
          if (!d) { this.dealInfo[todo[i]] = null; return; }
          const statuses = visibleActivities(d.activities, 'ROLE_FREIGHT_FORWARDER').filter(a => a.activityType?.category === 'STATUS');
          const last = [...statuses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          this.dealInfo[todo[i]] = {
            price: d.bid ? this.money(d.bid.price, d.bid.currencyCode) : '—',
            stage: last ? ((lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '—') : '—',
            stageDate: last?.createdAt ?? null,
            updatedToday: !!last && new Date(last.createdAt).toDateString() === new Date().toDateString(),
            delivered: isDeliveredByCodes(statuses.map(a => a.activityType?.code)),
          };
        });
        this.cdr.markForCheck();
      })
    );
  }

  onDealsPageChange(page: number): void {
    this.loadDealsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  viewBid(bid: FreightForwarderBidDto): void {
    // Переход к просмотру запроса с этой ставкой
    // Передаем параметр returnTo, чтобы знать, откуда вернуться
    this.router.navigate(['/freight-forwarder/request', bid.cargoRequestId], {
      queryParams: { returnTo: 'dashboard' }
    });
  }

  viewDeal(dealId: number): void {
    this.router.navigate(['/freight-forwarder/deal', dealId]);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getBidStatusText(bid: FreightForwarderBidDto): string {
    if (bid.bidStatus === 'ACCEPTED') {
      return this.acceptedText;
    } else if (bid.bidStatus === 'LOST') {
      return this.t('ffDash.statusLost', 'Не выбрана');
    }
    return this.t('ffDash.statusSent', 'Отправлена');
  }
  
  /** Цвет метки ставки: принята — зелёный, проиграна — красный, ожидает — жёлтый. */
  bidTone(bid: FreightForwarderBidDto): string {
    const cls = this.getBidStatusClass(bid);
    return cls === 'accepted' ? 'green' : cls === 'lost' ? 'red' : 'blue';
  }

  /** Цвет метки сделки по статусу. */
  dealTone(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'IN_PROGRESS': return 'blue';
      case 'CANCELLED': return 'red';
      case 'CREATED':
      case 'COMPLETED': return 'green';
      default: return 'amber';
    }
  }

  getBidStatusClass(bid: FreightForwarderBidDto): string {
    if (bid.bidStatus === 'ACCEPTED') {
      return 'accepted';
    } else if (bid.bidStatus === 'LOST') {
      return 'lost';
    }
    return 'pending';
  }
  
  getPricePositionText(bid: FreightForwarderBidDto): string {
    if (bid.bidStatus === 'LOST' && bid.pricePosition) {
      return `${bid.pricePosition}`;
    }
    return '';
  }
  
  hasPricePosition(bid: FreightForwarderBidDto): boolean {
    return bid.bidStatus === 'LOST' && bid.pricePosition !== null && bid.pricePosition !== undefined;
  }

  getDealStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'CREATED': 'Создана',
      'IN_PROGRESS': 'В процессе',
      'COMPLETED': 'Завершена',
      'CANCELLED': 'Отменена'
    };
    return statusMap[status] || status;
  }

}
